import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base-component/base-component.component';
import { MenuItem } from 'primeng/api';
import { AppRoutes } from 'src/app/core/models/routes.model';

@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.scss'],
    standalone: false
})
export class BreadcrumbsComponent extends BaseComponent {
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {};
  private routerEventsSubscription: Subscription;

  /**
   * Radice del percorso: la vetrina piani, che nell'ecommerce è la pagina di
   * partenza. Non esiste un livello superiore da cui scegliere l'applicazione.
   */
  private get homeSource(): MenuItem {
    return {
      icon: 'pi pi-home',
      url: `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.DASHBOARD}`,
      label: 'Piani',
    };
  }

  /**
   * Sulla vetrina il percorso è composto dalla sola radice: una briciola
   * singola non aiuta a orientarsi, quindi il componente sparisce.
   */
  get isVisible(): boolean {
    return this.breadcrumbs.length > 0;
  }

  constructor() {
    super();
    this.refresh();
    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.refresh());
  }

  /**
   * Va rifatto a ogni navigazione, non solo all'avvio: quali voci puntano alla
   * pagina corrente cambia insieme alla rotta.
   */
  private refresh(): void {
    this.home = this.toRedirectBreadcrumb(this.homeSource);
    this.setBreadcrumbsFromActiveRoute();
  }

  private setBreadcrumbsFromActiveRoute(): void {
    const rootRoute = this.route ?? this.router.routerState.root;
    this.breadcrumbs = this.deduplicateBreadcrumbs(
      this.collectBreadcrumbs(rootRoute).map((item: MenuItem) => this.toRedirectBreadcrumb(item))
    );
  }

  /** Path senza parametri di query né frammento, per confrontare due rotte. */
  private toPath(url: string): string {
    const path = url.split(/[?#]/)[0];
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
  }

  private toRedirectBreadcrumb(item: MenuItem): MenuItem {
    const target = item.routerLink ?? item.url;

    if (!target) {
      return item;
    }

    const targetUrl = Array.isArray(target)
      ? this.router.createUrlTree(target).toString()
      : String(target);

    // Una voce che punta alla pagina già aperta non deve essere cliccabile:
    // altrimenti sembra un collegamento e al click non accade nulla.
    if (this.toPath(targetUrl) === this.toPath(this.router.url)) {
      return { ...item, url: undefined, routerLink: undefined, styleClass: 'breadcrumb-current' };
    }

    return {
      ...item,
      url: undefined,
      command: () => {
        if (Array.isArray(target)) {
          this.router.navigate(target);
          return;
        }
        this.router.navigateByUrl(String(target));
      },
    };
  }

  private collectBreadcrumbs(
    route: ActivatedRoute | null | undefined,
    breadcrumbs: MenuItem[] = []
  ): MenuItem[] {
    if (!route) {
      return breadcrumbs;
    }

    const breadcrumbData = route.snapshot?.data?.['breadcrumb'];

    if (Array.isArray(breadcrumbData)) {
      breadcrumbs.push(...breadcrumbData);
    }

    return route.firstChild ? this.collectBreadcrumbs(route.firstChild, breadcrumbs) : breadcrumbs;
  }

  private deduplicateBreadcrumbs(items: MenuItem[]): MenuItem[] {
    return items.filter((item, index) => {
      if (index === 0) {
        return true;
      }

      const previousItem = items[index - 1];
      return !(previousItem.label === item.label && previousItem.icon === item.icon);
    });
  }

  override ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();
    super.ngOnDestroy();
  }
}
